import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import Page from '../Page';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import { GET_ME } from '../../graphql/me';
import {
  DELETE_GROUP_CALL,
  GET_GROUP_CALLS,
  UPDATE_GROUP_CALL,
} from '../../graphql/group_call';
import config from '../../config';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELED', label: 'Canceled' },
];

const ACCESS_OPTIONS = [
  { value: '', label: 'All access' },
  { value: 'PRO', label: 'Pro' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'PUBLIC', label: 'Public' },
];

const formatMonthlySchedule = (groupCall) => {
  if (!groupCall.dayOfMonth || !groupCall.startTime || !groupCall.endTime) {
    return 'Not set';
  }

  return `Monthly on day ${groupCall.dayOfMonth}`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'SCHEDULED':
      return 'success';
    case 'COMPLETED':
      return 'primary';
    case 'CANCELED':
      return 'default';
    case 'DRAFT':
    default:
      return 'warning';
  }
};

export default function GroupCallList() {
  const [statusFilter, setStatusFilter] = useState('SCHEDULED');
  const [accessFilter, setAccessFilter] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const { loading: meLoading, error: meError, data: meData } = useQuery(GET_ME);
  const trainerId = meData?.me?._id;

  const input = useMemo(
    () => ({
      trainerId,
      status: statusFilter || null,
      access: accessFilter || null,
      limit: 100,
    }),
    [accessFilter, statusFilter, trainerId]
  );

  const { loading, error, data, refetch } = useQuery(GET_GROUP_CALLS, {
    variables: { input },
    skip: !trainerId,
  });

  const [updateGroupCall, { loading: updating }] = useMutation(
    UPDATE_GROUP_CALL,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: () => {
        enqueueSnackbar('Group call completed.', { variant: 'success' });
        refetch();
      },
    }
  );

  const [deleteGroupCall, { loading: canceling }] = useMutation(
    DELETE_GROUP_CALL,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: () => {
        enqueueSnackbar(
          'Group call canceled. Member notifications will be sent.',
          { variant: 'success' }
        );
        refetch();
      },
    }
  );

  const handleComplete = (groupCall) => {
    updateGroupCall({
      variables: {
        id: groupCall._id,
        input: { status: 'COMPLETED' },
      },
    });
  };

  const handleCancel = (groupCall) => {
    if (!window.confirm('Cancel this group call?')) {
      return;
    }

    deleteGroupCall({ variables: { id: groupCall._id } });
  };

  if (meError) {
    return <ServerError message={meError.message || 'Failed to load'} />;
  }
  if (error) {
    return <ServerError message={error.message || 'Failed to load'} />;
  }
  if (meLoading || loading || !data?.groupCalls) {
    return <Loading />;
  }

  const actionLoading = updating || canceling;

  return (
    <Page title={`Group Calls : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <Box>
            <Typography variant="h3" color="textPrimary">
              Group Calls
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Publish and manage Pro group calls.
            </Typography>
            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
              Use a reusable recurring Zoom or Google Meet link. Members get
              calendar updates through their subscribed Tempo calendar feed.
            </Typography>
          </Box>
          <Button
            color="secondary"
            component={RouterLink}
            to="/trainer/group_calls/new"
            variant="contained"
          >
            New group call
          </Button>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="group-call-status-filter-label">Status</InputLabel>
            <Select
              label="Status"
              labelId="group-call-status-filter-label"
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="group-call-access-filter-label">Access</InputLabel>
            <Select
              label="Access"
              labelId="group-call-access-filter-label"
              onChange={(event) => setAccessFilter(event.target.value)}
              value={accessFilter}
            >
              {ACCESS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Card>
          <CardContent>
            {data.groupCalls.length === 0 ? (
              <Typography color="textSecondary">
                No group calls found for this filter.
              </Typography>
            ) : (
              <Box minWidth={1100}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Access</TableCell>
                      <TableCell>Schedule</TableCell>
                      <TableCell>Links</TableCell>
                      <TableCell>Calendar</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.groupCalls.map((groupCall) => {
                      const isDone = ['COMPLETED', 'CANCELED'].includes(
                        groupCall.status
                      );

                      return (
                        <TableRow hover key={groupCall._id}>
                          <TableCell>
                            <Link
                              color="inherit"
                              component={RouterLink}
                              to={`/trainer/group_calls/${groupCall._id}`}
                              variant="h6"
                            >
                              {groupCall.title || 'Untitled'}
                            </Link>
                            {groupCall.desc && (
                              <Typography color="textSecondary" variant="body2">
                                {groupCall.desc}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              color={getStatusColor(groupCall.status)}
                              label={groupCall.status || 'DRAFT'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{groupCall.access || 'PRO'}</TableCell>
                          <TableCell>
                            {formatMonthlySchedule(groupCall)}
                            <br />
                            <Typography color="textSecondary" variant="caption">
                              {groupCall.startTime || '--:--'} -{' '}
                              {groupCall.endTime || '--:--'}
                            </Typography>
                            {groupCall.timezone && (
                              <>
                                <br />
                                <Typography
                                  color="textSecondary"
                                  variant="caption"
                                >
                                  {groupCall.timezone}
                                </Typography>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {groupCall.meetingUrl ? (
                              <Link href={groupCall.meetingUrl} target="_blank">
                                Meeting
                              </Link>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="textSecondary">
                              {groupCall.calendarId || '-'}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              Sequence {groupCall.calendarSequence ?? 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Button
                                component={RouterLink}
                                size="small"
                                to={`/trainer/group_calls/${groupCall._id}`}
                              >
                                Edit
                              </Button>
                              <Button
                                disabled={actionLoading || isDone}
                                onClick={() => handleComplete(groupCall)}
                                size="small"
                              >
                                Complete
                              </Button>
                              <Button
                                color="error"
                                disabled={actionLoading || isDone}
                                onClick={() => handleCancel(groupCall)}
                                size="small"
                              >
                                Cancel
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}
